/**
 * Print format constants: A1–A6, 10×15, 15×20, 7.6×9, sticker 4×4–6×6, credit card 8.6×5.4, 2:3, 3:2, 3:4, 4:3.
 * Net size in mm; 3mm bleed applied in mmToPx: finalMm = mm + 6, px = round((finalMm/25.4)*dpi).
 * Used by: to-view-value, to-booth-value, configuration-update.
 */
const BLEED_MM = 6; // 3mm per side (6mm total)
const MM_PER_INCH = 25.4;

/** formatId -> { mmWidth, mmHeight } (net size in mm) */
export const PRINT_FORMATS = {
    portrait: { mmWidth: 100, mmHeight: 150 },       // 2:3
    landscape: { mmWidth: 150, mmHeight: 100 },      // 3:2
    portrait_3_4: { mmWidth: 150, mmHeight: 200 },   // 3:4
    landscape_4_3: { mmWidth: 200, mmHeight: 150 },  // 4:3
    a1_portrait: { mmWidth: 594, mmHeight: 841 },
    a1_landscape: { mmWidth: 841, mmHeight: 594 },
    a2_portrait: { mmWidth: 420, mmHeight: 594 },
    a2_landscape: { mmWidth: 594, mmHeight: 420 },
    a3_portrait: { mmWidth: 297, mmHeight: 420 },
    a3_landscape: { mmWidth: 420, mmHeight: 297 },
    a4_portrait: { mmWidth: 210, mmHeight: 297 },
    a4_landscape: { mmWidth: 297, mmHeight: 210 },
    a5_portrait: { mmWidth: 148, mmHeight: 210 },
    a5_landscape: { mmWidth: 210, mmHeight: 148 },
    a6_portrait: { mmWidth: 105, mmHeight: 148 },
    a6_landscape: { mmWidth: 148, mmHeight: 105 },
    "10x15": { mmWidth: 100, mmHeight: 150 },
    "15x10": { mmWidth: 150, mmHeight: 100 },
    "15x20": { mmWidth: 150, mmHeight: 200 },
    "20x15": { mmWidth: 200, mmHeight: 150 },
    "7.6x9": { mmWidth: 76, mmHeight: 90 },
    "4x4": { mmWidth: 40, mmHeight: 40 },
    "4.5x4.5": { mmWidth: 45, mmHeight: 45 },
    "5x5": { mmWidth: 50, mmHeight: 50 },
    "5.5x5.5": { mmWidth: 55, mmHeight: 55 },
    "6x6": { mmWidth: 60, mmHeight: 60 },
    // Credit card / ID card (ISO/IEC 7810 ID-1)
    "8.6x5.4": { mmWidth: 85.6, mmHeight: 54 },
    "5.4x8.6": { mmWidth: 54, mmHeight: 85.6 }
};

export const PRINT_DPI_VALUES = [72, 150, 300, 600];

/**
 * @param {number} mm - net size in mm
 * @param {number} dpi
 * @param {boolean} [includeBleed=true] - add 6mm (3mm per side)
 * @returns {number}
 */
export function mmToPx(mm, dpi, includeBleed = true) {
    const bleedTotal = includeBleed ? BLEED_MM : 0;
    const finalMm = mm + bleedTotal;
    return Math.round((finalMm / MM_PER_INCH) * dpi);
}

/**
 * @param {string} formatId
 * @param {number} dpi
 * @returns {{ width: number, height: number }}
 */
export function getPixelsForFormat(formatId, dpi) {
    const f = PRINT_FORMATS[formatId];
    if (!f) return { width: 1240, height: 1844 };
    return {
        width: mmToPx(f.mmWidth, dpi, true),
        height: mmToPx(f.mmHeight, dpi, true)
    };
}

/** Options for format md-select: { id, label } */
export const PRINT_FORMAT_OPTIONS = [
    { id: "portrait", label: "Portrait (2:3)" },
    { id: "landscape", label: "Landscape (3:2)" },
    { id: "portrait_3_4", label: "Portrait (3:4)" },
    { id: "landscape_4_3", label: "Landscape (4:3)" },
    { id: "a1_portrait", label: "DIN A1 Portrait" },
    { id: "a1_landscape", label: "DIN A1 Landscape" },
    { id: "a2_portrait", label: "DIN A2 Portrait" },
    { id: "a2_landscape", label: "DIN A2 Landscape" },
    { id: "a3_portrait", label: "DIN A3 Portrait" },
    { id: "a3_landscape", label: "DIN A3 Landscape" },
    { id: "a4_portrait", label: "DIN A4 Portrait" },
    { id: "a4_landscape", label: "DIN A4 Landscape" },
    { id: "a5_portrait", label: "DIN A5 Portrait" },
    { id: "a5_landscape", label: "DIN A5 Landscape" },
    { id: "a6_portrait", label: "DIN A6 Portrait" },
    { id: "a6_landscape", label: "DIN A6 Landscape" },
    { id: "10x15", label: "10×15 cm" },
    { id: "15x10", label: "15×10 cm" },
    { id: "15x20", label: "15×20 cm" },
    { id: "20x15", label: "20×15 cm" },
    { id: "7.6x9", label: "7.6×9 cm" }
];

/** Options for sticker format md-select: { id, label } */
export const STICKER_FORMAT_OPTIONS = [
    { id: "4x4", label: "4×4 cm" },
    { id: "4.5x4.5", label: "4.5×4.5 cm" },
    { id: "5x5", label: "5×5 cm" },
    { id: "5.5x5.5", label: "5.5×5.5 cm" },
    { id: "6x6", label: "6×6 cm" }
];

/** Options for credit card / ID card format md-select: { id, label } */
export const CARD_FORMAT_OPTIONS = [
    { id: "8.6x5.4", label: "Card Landscape (8.6×5.4 cm)" },
    { id: "5.4x8.6", label: "Card Portrait (5.4×8.6 cm)" }
];

/** Grouped options for format md-select: { label, options } */
export const PRINT_FORMAT_OPTION_GROUPS = [
    { label: "Print formats", options: PRINT_FORMAT_OPTIONS },
    { label: "Sticker formats", options: STICKER_FORMAT_OPTIONS },
    { label: "Credit card / ID card formats", options: CARD_FORMAT_OPTIONS }
];

/** Options for DPI md-select: { value, label } */
export const PRINT_DPI_OPTIONS = [
    { value: 72, label: "72 (Preview)" },
    { value: 150, label: "150 (Draft)" },
    { value: 300, label: "300 (Print Std.)" },
    { value: 600, label: "600 (High-End)" }
];

/** Legacy formatIds that map to old layoutAspectRatio (for reverse lookup) */
export const LEGACY_FORMAT_IDS = ["portrait", "landscape", "portrait_3_4", "landscape_4_3"];

/**
 * Short label for formatId, e.g. for "Allow printing (…)" toggles: "2:3", "A4", "10×15".
 */
export function getFormatShortLabel(formatId) {
    if (!formatId) return "";
    const cm = {
        "10x15": "10×15", "15x10": "15×10", "15x20": "15×20", "20x15": "20×15", "7.6x9": "7.6×9",
        "4x4": "4×4", "4.5x4.5": "4.5×4.5", "5x5": "5×5", "5.5x5.5": "5.5×5.5", "6x6": "6×6",
        "8.6x5.4": "8.6×5.4", "5.4x8.6": "5.4×8.6"
    };
    if (cm[formatId]) return cm[formatId];
    const m = String(formatId).match(/^a(\d)_(?:portrait|landscape)$/);
    return m ? "A" + m[1] : "";
}

/**
 * Reverse lookup: from finalSize.width/height finds best-matching formatId and dpi.
 * Used for migrating old configs (pixels only) and when printLayoutFormat/printDpi are missing.
 */
export function getFormatAndDpiFromPixels(width, height) {
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
        return { formatId: "portrait", dpi: 300 };
    }
    let best = { formatId: null, dpi: 300, diff: Infinity };
    for (const dpi of PRINT_DPI_VALUES) {
        for (const [id] of Object.entries(PRINT_FORMATS)) {
            const p = getPixelsForFormat(id, dpi);
            const diff = Math.abs(p.width - width) + Math.abs(p.height - height);
            if (diff < best.diff) {
                best = { formatId: id, dpi, diff };
            }
        }
    }
    if (best.formatId) return { formatId: best.formatId, dpi: best.dpi };
    return { formatId: width > height ? "landscape" : "portrait", dpi: 300 };
}



// WEBPACK FOOTER //
// ./sources/constants/print-formats.constant.js